<template>
  <div v-loading="loading" element-loading-spinner="atom-audit-loader">
    <div style="float: left; text-align: left; margin: 0px 0px 10px 10px; font-weight: 400;">Account Numbers for {{ header }}</div>

    <v-client-table class="hiaTable" ref="acctListGrid" id="acctListGrid" :data="acctList" :columns="columns" :options="tableOptions">
      <template slot="patientNumber" slot-scope="props">
        <template v-if="activeAccountLink">
          <router-link v-if="ipList" :to="`/reviews/inpatient/${props.row.drgtableId}`" target="_blank">{{ props.row.patientNumber }} </router-link>
          <router-link v-if="proList" :to="`/reviews/pro/${props.row.proRecordId}`" target="_blank">{{ props.row.patientNumber }} </router-link>
          <router-link v-if="opList" :to="`/reviews/outpatient/${props.row.ascTableID}`" target="_blank">{{ props.row.patientNumber }} </router-link>
        </template>
        <template v-else>
          <span>{{ props.row.patientNumber }}</span>
        </template>
      </template>

      <template slot="dschDate" slot-scope="props">
        {{ props.row.dschDate ? formatDate(props.row.dschDate) : '' }}
      </template>

      <template slot="dateOfService" slot-scope="props">
        {{ props.row.dateOfService ? formatDate(props.row.dateOfService) : '' }}
      </template>

      <template slot="worksheet" slot-scope="props">
        <el-button v-if="ipList" @click="handleViewWorksheets(props.row.drgtableId)" plain round><i class="mdi mdi-open-in-new tableIcon"></i>Worksheet </el-button>
        <el-button v-if="proList" @click="handleViewWorksheetsPro(props.row.proRecordId)" plain round><i class="mdi mdi-open-in-new tableIcon"></i>Worksheet </el-button>
        <el-button v-if="opList" @click="handleViewWorksheetsOP(props.row.ascTableID)" plain round><i class="mdi mdi-open-in-new tableIcon"></i>Worksheet </el-button>
      </template>
    </v-client-table>
  </div>
</template>

<script>
  import axios from '@/utilities/axios-global'
  import dateHelper from '@/mixins/date-helper'

  import { mapState } from 'vuex'
  export default {
    name: 'acctList',
    props: ['header', 'drgTableIds', 'proRecordIds', 'ascTableIds', 'showRebuttalStatus'],
    mixins: [dateHelper],
    data() {
      return {
        acctList: [],
        loading: false,
        tableOptions: {
          filterByColumn: true,
          headings: {
            patientNumber: 'Patient Number',
            mrNumber: 'MR Number',
            payName: 'Payor',
            dschDate: 'Discharge Date',
            dateOfService: 'Date of Service',
            reasonForChange: 'Reason For Change',
            providerName: 'Provider',
            originalDrg: 'Facility DRG',
            newDrg: 'Rec. DRG',
            rebuttalStatusDescription: 'Rebuttal Status',
            worksheet: ''
          },
          perPage: 20,
          texts: {
            filter: '',
            filterBy: '...'
          },
          highlightMatches: true,
          sortIcon: { is: 'none' }
        }
      }
    },
    methods: {
      async getData() {
        const payload = {
          drgTableIds: this.drgTableIds,
          proRecordIds: this.proRecordIds,
          ascTableIds: this.ascTableIds,
          includeAccts: true,
          statType: ''
        }
        try {
          this.loading = true
          let endpoint = '/reports/dashboard/accountDetailsIP'
          if (this.proList) {
            endpoint = '/reports/dashboard/accountDetailsPro'
          } else if (this.opList) {
            endpoint = '/reports/dashboard/accountDetailsOP'
          }
          const response = await axios.post(this.baseApiUrl + endpoint, payload)
          this.acctList = response.data
        } catch (err) {
          this.acctList = []
        } finally {
          this.loading = false
        }
      },
      handleViewWorksheets(drgTableId) {
        const cacheBuster = new Date().getTime()
        const worksheetPath = this.inpatientReviewer() && this.ipList ? `/reviews/reports/inpatient/worksheets?fullscreen=true&dt=${cacheBuster}&ids=${drgTableId}` : `/my-projects/inpatient/worksheet/${drgTableId}?dt=${cacheBuster}&fullscreen=true`
        this.worksheetWindow = window.open(worksheetPath, 'codebook', 'height=850,width=1600')
      },
      handleViewWorksheetsPro(proRecordId) {
        const cacheBuster = new Date().getTime()
        const worksheetPath = this.providerReviewer() && this.proList ? `/reviews/reports/pro/worksheets?fullscreen=true&dt=${cacheBuster}&ids=${proRecordId}` : `/my-projects/pro/worksheet/${proRecordId}?dt=${cacheBuster}&fullscreen=true`
        this.worksheetWindow = window.open(worksheetPath, 'codebook', 'height=850,width=1600')
      },
      handleViewWorksheetsOP(ascTableId) {
        const cacheBuster = new Date().getTime()
        const worksheetPath = (this.outpatientReviewer() && this.opList) || (this.ambulatoryReviewer() && this.opList) ? `/reviews/reports/outpatient/worksheets?fullscreen=true&dt=${cacheBuster}&ids=${ascTableId}` : `/my-projects/outpatient/worksheet/${ascTableId}?dt=${cacheBuster}&fullscreen=true`
        this.worksheetWindow = window.open(worksheetPath, 'codebook', 'height=850,width=1600')
      }
    },
    created: async function() {
      await this.getData()
    },
    computed: {
      ...mapState('user/', ['baseApiUrl']),
      columns() {
        const cols = ['patientNumber', 'mrNumber', 'payName']
        if (this.ipList) {
          cols.push('dschDate')
        } else {
          cols.push('dateOfService')
        }
        cols.push('reasonForChange')
        if (this.proList) {
          cols.push('providerName')
        }
        if (this.ipList) {
          cols.push('originalDrg', 'newDrg')
        }
        if (this.showRebuttalStatus) {
          cols.push('rebuttalStatusDescription')
        }
        cols.push('worksheet')
        return cols
      },
      ipList() {
        if (this.drgTableIds) {
          return true
        } else {
          return false
        }
      },
      proList() {
        if (this.proRecordIds) {
          return true
        } else {
          return false
        }
      },
      opList() {
        if (this.ascTableIds) {
          return true
        } else {
          return false
        }
      },
      activeAccountLink() {
        if ((this.inpatientReviewer() && this.ipList) || (this.outpatientReviewer() && this.opList) || (this.ambulatoryReviewer() && this.opList) || (this.providerReviewer() && this.proList)) {
          return true
        }
        return false
      }
    }
  }
</script>

<style scoped>
  .disable-link {
    pointer-events: none;
  }
</style>
